import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import { RouterPaths } from 'src/components/Router/Router.constants';
import type { tAlertCtaSurface } from 'src/hooks/analytics/alertCenterAnalytics.constants';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useUserAvailability } from 'src/hooks/availability/useUserAvailability.hook';
import { getPlanName } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import {
  resolveAlertSurface,
  useAlertCenterContext,
} from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useAlertCtaAnalytics } from './AlertCenter.exposure.hook';

/**
 * Distinguishes this surface inside the shared `addon_*` analytics family,
 * alongside the legacy banners' 'funnel' and 'chat' sources.
 */
const ANALYTICS_SOURCE = 'alert-center';

/**
 * Carries out an alert's call to action, wherever the alert is rendered.
 *
 * Shared by the wide strip, the panel and the detail modal deliberately: the
 * same declaration appears in all of them, and two copies of this dispatch
 * would drift into "the CTA works in the bar but not in the panel" — a class
 * of bug nobody reports because the alert still looks right.
 *
 * U4 (RET-1878) — `action` CTAs resolve here too, against app-global seams
 * (`tAlertCtaAction` is the closed set), so every surface executes every CTA
 * with nothing to inject. Subscribing to ModalsContext does re-render these
 * surfaces on unrelated modal toggles; accepted — the strip and rows are
 * cheap, and the legacy quota card pays the same cost today.
 *
 * RET-1956 — the ONE exception is `detail`: the open detail is surface state
 * (AlertBell owns it, the Avisos tab owns its own), so this dispatch cannot
 * resolve it globally the way it resolves the archive modal. The opener is
 * injected instead, and the surface passes the clicked element along so focus
 * can return to it. A surface that renders CTAs without injecting one simply
 * cannot host a `detail` CTA — which is why no `wide` declaration carries one.
 */
export type tAlertCtaOptions = {
  /** Opens this alert's detail modal, remembering the element that asked. */
  onOpenDetail?: (alert: tLiveAlert, invoker: HTMLElement | null) => void;
  /**
   * RET-1957 (U4) — who is dispatching. Naming a surface opts INTO
   * `front.alerts.row.cta_clicked` with that value as `source`; the wide strip
   * names none because it emits its own `banner.cta_clicked` for the same
   * click. It is declared rather than inferred from the alert: the bell lists
   * waiting P0s, whose placement is `wide`, and inferring would lose them.
   */
  surface?: tAlertCtaSurface;
};

export function useAlertCta(
  options: tAlertCtaOptions = {},
): (alert: tLiveAlert, invoker?: HTMLElement | null) => void {
  const { onOpenDetail, surface } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const { openSupport, requestStageThresholdEdit } = useAlertCenterContext();
  const {
    setShowArchiveLeadsModal,
    setWhatsAppBusinessConnectionModalIsOpen,
    setMetaConnectionModalIsOpen,
    setMetaConnectionModalReconnectRequested,
  } = useModalsContext();
  const { setAvailability } = useUserAvailability();
  const { subscription } = useSubscriptionContext();
  const { trackEvent } = useAnalytics();
  // RET-1957 (U4) — the center's own CTA event, emitted from the one place
  // every surface's click passes through. It lives in the exposure seam with
  // the family's other emitters; see `useAlertCtaAnalytics` for what it skips.
  const trackCtaClick = useAlertCtaAnalytics(surface);
  const planCode = subscription?.planCode;

  return useCallback(
    (alert: tLiveAlert, invoker: HTMLElement | null = null) => {
      const { cta } = alert.copy;
      if (!cta) return;
      // Before the branch, so every kind counts: the legacy `addon_*` events
      // below report the DESTINATION, this one reports the click.
      trackCtaClick(alert);
      if (cta.kind === 'intercom') {
        openSupport(cta.message);
        return;
      }
      if (cta.kind === 'detail') {
        // The same opener the row's text area uses — one detail, one owner of
        // its state, so the CTA and the row cannot disagree about what is open.
        onOpenDetail?.(alert, invoker);
        return;
      }
      if (cta.kind === 'navigate' && cta.target) {
        // Router navigation, never window.location: a full reload here would
        // tear down every provider the shell just hydrated.
        navigate(cta.target);
        return;
      }
      if (cta.kind === 'external' && cta.target) {
        window.open(cta.target, '_blank', 'noopener,noreferrer');
        return;
      }
      if (cta.kind !== 'action') return;
      if (cta.action === 'archive-leads') {
        // Analytics parity with ActiveLeadsQuotaBanner.handleArchiveClick,
        // including its planCode gate — only the source is this surface's own.
        if (planCode) {
          trackEvent('front.settings.addon_archive_initiated', {
            plan: getPlanName(planCode),
            source: ANALYTICS_SOURCE,
          });
        }
        // Global modal (ModalsProvider) — flipping the flag is all it takes.
        setShowArchiveLeadsModal(true);
        return;
      }
      if (cta.action === 'open-stage-threshold') {
        // U9 (KTD6) — the only action whose destination is not app-global: the
        // stage modal belongs to the board. The provider carries the stage the
        // board reported red, so this dispatch names none.
        requestStageThresholdEdit();
        // From a surface with no board under it the request would have nothing
        // to pick it up, so take the user to the funnel first. Navigating from
        // the board itself would push a history entry and remount the board
        // underneath the modal it is about to open.
        if (resolveAlertSurface(location.pathname) !== 'funnels') {
          navigate(RouterPaths.funnels);
        }
        return;
      }
      if (cta.action === 'connect-wapi') {
        // The WhatsApp Business API reconnect lives in a modal ModalsProvider
        // mounts app-wide, so it opens from any surface. No route carries it:
        // the WhatsApp API page reports the connection, it does not restore it.
        setWhatsAppBusinessConnectionModalIsOpen(true);
        return;
      }
      if (cta.action === 'reconnect-meta') {
        // MetaConnectionModal reads metaConnectionModalReconnectRequested
        // (ModalsContext, not its own narrower context -- see
        // MetaConnectionModa.types.ts for why) and sets its OWN step to 2
        // (MetaStepInfo, the actual "start the Facebook OAuth flow" screen)
        // in response; this dispatch only needs to raise the flag and open
        // the modal, never the step directly.
        setMetaConnectionModalReconnectRequested(true);
        setMetaConnectionModalIsOpen(true);
        return;
      }
      if (cta.action === 'restore-availability') {
        // Same event the in-page banner emits; `source` tells the surfaces
        // apart without splitting the funnel.
        trackEvent('front.inactive_user_banner.button.click_back_to_active', {
          source: ANALYTICS_SOURCE,
        });
        setAvailability(true);
      }
    },
    [
      navigate,
      location.pathname,
      openSupport,
      onOpenDetail,
      requestStageThresholdEdit,
      setShowArchiveLeadsModal,
      setWhatsAppBusinessConnectionModalIsOpen,
      setMetaConnectionModalIsOpen,
      setMetaConnectionModalReconnectRequested,
      setAvailability,
      planCode,
      trackCtaClick,
      trackEvent,
    ],
  );
}
